/*
 * Hugami scroll motion. One observer for the whole page:
 * - [data-inview] elements (Feature blocks) get .is-inview once, so their icon draws itself;
 * - with "Reveal content on scroll" on, cards, steps, quotes, tables, images and carousels are tagged
 *   [data-reveal-item] and rise in once. Siblings that enter together are staggered via --reveal-index.
 * A card (a group with its own background) reveals as one piece: its children don't reveal again.
 */
(() => {
  const root = document.documentElement;
  const REVEAL = '.feature, .pull-quote, .comparison-table, .review-carousel, .image-block, .seal';
  const SKIP = '.ticker, .hero, header, footer, .timeline, [data-reveal-item]';

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-inview');
              observer.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -12% 0px' }
        )
      : null;

  const watch = (element) => (observer ? observer.observe(element) : element.classList.add('is-inview'));

  const tagReveals = (scope) => {
    if (!root.hasAttribute('data-reveal')) return;
    for (const element of scope.querySelectorAll(REVEAL)) {
      if (element.parentElement?.closest(SKIP) || element.closest('.ticker, .hero, header, footer, .timeline')) continue;
      // Inside a card, the card reveals instead of each child
      const card = element.closest('.group-block[class*="color-custom-"]');
      const target = card && !card.parentElement.closest('[data-reveal-item]') ? card : element;
      if (target.hasAttribute('data-reveal-item')) continue;
      target.setAttribute('data-reveal-item', '');
      const siblings = [...target.parentElement.children].filter((child) => child.hasAttribute('data-reveal-item'));
      target.style.setProperty('--reveal-index', Math.min(siblings.indexOf(target), 5));
      watch(target);
    }
  };

  const scan = (scope = document) => {
    tagReveals(scope);
    for (const element of scope.querySelectorAll('[data-inview]:not(.is-inview)')) watch(element);
  };

  scan();
  // Theme editor: sections re-render without a page load
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();

// Review cards with a long quote: "Read more" shows only when the quote is cut, and opens it in place
(() => {
  const watch = (scope = document) => {
    for (const button of scope.querySelectorAll('[data-read-more]')) {
      const card = button.closest('.review-card');
      const quote = card?.querySelector('.review-card__quote p');
      if (!quote) continue;
      const check = () => {
        if (!card.classList.contains('is-expanded')) button.hidden = quote.scrollHeight <= quote.clientHeight + 1;
      };
      check();
      new ResizeObserver(check).observe(quote);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-read-more]');
    if (!button) return;
    const open = button.closest('.review-card').classList.toggle('is-expanded');
    button.textContent = open ? button.dataset.less : button.dataset.more;
    button.setAttribute('aria-expanded', String(open));
  });

  watch();
  document.addEventListener('shopify:section:load', (event) => watch(event.target));
})();

